import { Directive, EventEmitter, Host, Input, Output, TemplateRef, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { UnsubscribeOnDestroy } from '@app/util/lifecycle-hooks/lifecycle-hooks';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { WorkflowQueryDto } from '@interfaces/queries';

export interface TemplateContext<T> {
	$implicit: T;
	selectedValue: T;
	next?: () => void;
	previous?: () => void;
}

@Directive({
	selector: '[appValueSelect]',
})
// eslint-disable-next-line rxjs-angular/prefer-composition
export class ValueSelectDirective<T> {
	@UnsubscribeOnDestroy
	private readonly subscription = new Subscription();
	
	private readonly values$ = new BehaviorSubject<T[]>([]);
	private readonly emptyTemplate$ = new BehaviorSubject<TemplateRef<undefined> | null>(null);
	
	@Input('appValueSelect')
	public set values(values: T[]) {
		if (Array.isArray(values)) {
			this.values$.next(values);
		} else {
			this.values$.next([]);
		}
	}
	
	@Input('appValueSelectEmpty')
	public set empty(template: TemplateRef<undefined> | undefined) {
		this.emptyTemplate$.next(template ?? null);
	}
	
	@Input('appValueSelectChange')
	public change?: (query: T | null) => void;
	
	constructor(
		@Host() viewContainerRef: ViewContainerRef,
		@Host() template: TemplateRef<TemplateContext<T>>,
	) {
		const selected$ = new BehaviorSubject(0);
		
		const templateContext$: Observable<TemplateContext<T> | null> = combineLatest([
			this.values$,
			selected$,
		]).pipe(
			map(([values, selected]) => {
				if (values.length === 0) {
					return null;
				}
				
				if (selected >= values.length) {
					selected = values.length - 1;
				}
				if (selected < 0) {
					selected = 0;
				}
				
				let next: TemplateContext<T>['next'] = undefined;
				let previous: TemplateContext<T>['previous'] = undefined;
				
				if (selected + 1 < values.length) {
					next = () => selected$.next(selected + 1);
				}
				
				if (selected > 0) {
					previous = () => selected$.next(selected - 1);
				}
				
				const selectedValue = values[selected];
				
				return {
					$implicit: selectedValue,
					selectedValue,
					previous,
					next,
				};
			}),
		);
		
		this.subscription.add(
			combineLatest([
				this.emptyTemplate$,
				templateContext$,
			]).subscribe(([empty, ctx]) => {
				viewContainerRef.clear();
				
				if (ctx) {
					viewContainerRef.createEmbeddedView(template, ctx);
				} else if (empty) {
					viewContainerRef.createEmbeddedView(empty);
				}
			}),
		);
		
		this.subscription.add(
			templateContext$.pipe(
				map((ctx) => ctx?.selectedValue),
				distinctUntilChanged(),
			).subscribe(selectedValue => this.change?.(selectedValue ?? null)),
		);
	}
	
}
